import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp, MessageSquare, Loader2, ArrowRight, Filter, Edit3, FileText } from 'lucide-react';
import { approvalAPI, fetchPaged, ocAmendmentAPI } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../ui/Pagination';
import { toast } from 'sonner';

const statusColors = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
};

const ApprovalsModule = () => {
  const [activeTab, setActiveTab] = useState('customers');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [expanded, setExpanded] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const canApprove = ['Admin', 'Manager'].includes(user.role);
  const { hasPermission } = usePermissions();
  const canApproveAmendment = hasPermission('oc.approve_amendment');

  // OC Amendment state
  const [amendments, setAmendments] = useState([]);
  const [amendmentStats, setAmendmentStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [amendFilter, setAmendFilter] = useState('Pending');
  const [amendRejectModal, setAmendRejectModal] = useState(null);
  const [amendRejectReason, setAmendRejectReason] = useState('');
  const [amendApproveModal, setAmendApproveModal] = useState(null);
  const [amendApproveNotes, setAmendApproveNotes] = useState('');
  const [amendLoading, setAmendLoading] = useState(true);
  const [amendExpanded, setAmendExpanded] = useState(null);

  useEffect(() => {
    if (activeTab === 'customers') fetchData();
    else fetchAmendments();
  }, [filter, page, pageSize, activeTab, amendFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (filter) params.status_filter = filter;
      const [result, st] = await Promise.all([
        fetchPaged('/customer-approvals', params),
        approvalAPI.getStats()
      ]);
      setRequests(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(st);
    } catch (err) {
      console.error('Failed to load approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAmendments = useCallback(async () => {
    try {
      setAmendLoading(true);
      const statusParam = amendFilter === 'all' ? 'all' : amendFilter;
      const [result, st] = await Promise.all([
        ocAmendmentAPI.list({ status: statusParam, page: 1, page_size: 100 }),
        ocAmendmentAPI.stats(),
      ]);
      setAmendments(result.items || []);
      setAmendmentStats(st);
    } catch (err) {
      console.error('Failed to load amendments:', err);
    } finally {
      setAmendLoading(false);
    }
  }, [amendFilter]);

  const handleApprove = async (id) => {
    try {
      setProcessing(id);
      await approvalAPI.approve(id, { reason: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return alert('Please provide a reason');
    try {
      setProcessing(rejectModal);
      await approvalAPI.reject(rejectModal, { reason: rejectReason });
      setRejectModal(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveAmendment = async () => {
    try {
      setProcessing(amendApproveModal);
      await ocAmendmentAPI.approve(amendApproveModal, amendApproveNotes);
      toast.success('Amendment approved and applied');
      setAmendApproveModal(null);
      setAmendApproveNotes('');
      fetchAmendments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve amendment');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectAmendment = async () => {
    if (!amendRejectReason.trim()) return toast.error('Please provide a reason for rejection');
    try {
      setProcessing(amendRejectModal);
      await ocAmendmentAPI.reject(amendRejectModal, amendRejectReason);
      toast.success('Amendment rejected');
      setAmendRejectModal(null);
      setAmendRejectReason('');
      fetchAmendments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject amendment');
    } finally {
      setProcessing(null);
    }
  };

  const fieldLabels = { name: 'Company Name', contact: 'Contact Person', phone: 'Phone', email: 'Email', gstin: 'GSTIN', pan: 'PAN', state: 'State', city: 'City', pincode: 'Pincode', address: 'Address', type: 'Type', credit_days: 'Credit Days' };

  const renderDiff = (req) => {
    const fields = Object.keys(fieldLabels);
    const newData = req.customer_data || {};
    const oldData = req.original_customer_data || {};
    const isEdit = req.request_type === 'Edit';
    return (
      <div className="grid gap-2 mt-3">
        {fields.map(f => {
          const newVal = String(newData[f] ?? '');
          const oldVal = String(oldData[f] ?? '');
          const changed = isEdit && oldVal !== newVal;
          return (
            <div key={f} className={`flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs ${changed ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'}`}>
              <span className="w-28 font-medium text-gray-500 flex-shrink-0">{fieldLabels[f]}</span>
              {isEdit && changed ? (
                <>
                  <span className="text-red-400 line-through">{oldVal || '-'}</span>
                  <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="text-emerald-700 font-semibold">{newVal || '-'}</span>
                </>
              ) : (
                <span className="text-gray-900">{newVal || '-'}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-5 lg:p-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900" data-testid="approvals-title" style={{ fontFamily: 'DM Sans' }}>Approvals</h1>
        <p className="text-xs text-gray-500 mt-0.5">Review and manage approval requests</p>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit" data-testid="approval-main-tabs">
        <button onClick={() => setActiveTab('customers')}
          className={`px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${activeTab === 'customers' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Customer Requests
          {stats.pending > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white rounded-full bg-amber-500">{stats.pending}</span>}
        </button>
        <button onClick={() => setActiveTab('amendments')} data-testid="amendments-tab"
          className={`px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${activeTab === 'amendments' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          OC Amendments
          {amendmentStats.pending > 0 && <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white rounded-full bg-purple-500">{amendmentStats.pending}</span>}
        </button>
      </div>

      {/* =================== Customer Approvals Tab =================== */}
      {activeTab === 'customers' && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Pending', value: stats.pending, color: '#f59e0b', bg: 'bg-amber-50' },
              { label: 'Approved', value: stats.approved, color: '#059669', bg: 'bg-emerald-50' },
              { label: 'Rejected', value: stats.rejected, color: '#dc2626', bg: 'bg-red-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 border`} style={{ borderColor: s.color + '30' }}>
                <p className="text-[11px] font-medium text-gray-500">{s.label}</p>
                <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit" data-testid="approval-filters">
            {['Pending', 'Approved', 'Rejected', ''].map(f => (
              <button key={f} onClick={() => { setFilter(f); setPage(1); }} data-testid={`filter-${f || 'all'}`}
                className={`px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {f || 'All'}
                {f === 'Pending' && stats.pending > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white rounded-full" style={{ backgroundColor: '#f59e0b' }}>{stats.pending}</span>
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading...</span></div>
          ) : requests.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100"><Filter className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No {filter.toLowerCase() || ''} requests found</p></div>
          ) : (
            <div className="space-y-3">
              {requests.map(req => (
                <div key={req.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-sm transition-shadow" data-testid={`approval-card-${req.id}`}>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${req.request_type === 'Add' ? 'bg-blue-50' : 'bg-purple-50'}`}>
                        <span className="text-xs font-bold" style={{ color: req.request_type === 'Add' ? '#24549c' : '#7c3aed' }}>{req.request_type === 'Add' ? '+' : 'E'}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{req.customer_data?.name || 'Unknown'}</h3>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColors[req.status]}`}>{req.status}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5">{req.request_type} request by <strong>{req.submitted_by_name}</strong> &middot; {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        {req.remarks && <p className="text-[11px] text-gray-400 mt-0.5 italic">"{req.remarks}"</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {req.status === 'Pending' && canApprove && (
                        <>
                          <button onClick={() => handleApprove(req.id)} disabled={processing === req.id} data-testid={`approve-btn-${req.id}`}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                            {processing === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Approve
                          </button>
                          <button onClick={() => { setRejectModal(req.id); setRejectReason(''); }} data-testid={`reject-btn-${req.id}`}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                      {req.status === 'Rejected' && req.rejection_reason && (
                        <div className="flex items-center gap-1 text-[11px] text-red-500 max-w-[200px]" title={req.rejection_reason}><MessageSquare className="w-3 h-3 flex-shrink-0" /><span className="truncate">{req.rejection_reason}</span></div>
                      )}
                      {req.status === 'Approved' && req.reviewed_by_name && <span className="text-[11px] text-emerald-600">by {req.reviewed_by_name}</span>}
                      <button onClick={() => setExpanded(expanded === req.id ? null : req.id)} data-testid={`expand-btn-${req.id}`} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        {expanded === req.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                  {expanded === req.id && (
                    <div className="px-4 pb-4 border-t border-gray-50 pt-3">
                      <p className="text-[11px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">{req.request_type === 'Edit' ? 'Changes (highlighted)' : 'Customer Details'}</p>
                      {renderDiff(req)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {requests.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
        </>
      )}

      {/* =================== OC Amendments Tab =================== */}
      {activeTab === 'amendments' && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Pending', value: amendmentStats.pending, color: '#a855f7', bg: 'bg-purple-50' },
              { label: 'Approved', value: amendmentStats.approved, color: '#059669', bg: 'bg-emerald-50' },
              { label: 'Rejected', value: amendmentStats.rejected, color: '#dc2626', bg: 'bg-red-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 border`} style={{ borderColor: s.color + '30' }}>
                <p className="text-[11px] font-medium text-gray-500">{s.label}</p>
                <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit" data-testid="amendment-filters">
            {['Pending', 'Approved', 'Rejected', 'all'].map(f => (
              <button key={f} onClick={() => setAmendFilter(f)} data-testid={`amend-filter-${f}`}
                className={`px-4 py-2 text-[11px] font-semibold rounded-lg transition-all ${amendFilter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {f === 'all' ? 'All' : f}
                {f === 'Pending' && amendmentStats.pending > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white rounded-full bg-purple-500">{amendmentStats.pending}</span>
                )}
              </button>
            ))}
          </div>

          {amendLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /><span className="ml-2 text-sm text-gray-500">Loading amendments...</span></div>
          ) : amendments.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100"><Edit3 className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No {amendFilter !== 'all' ? amendFilter.toLowerCase() : ''} OC amendments found</p></div>
          ) : (
            <div className="space-y-3">
              {amendments.map(a => (
                <div key={a.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-sm transition-shadow" data-testid={`amendment-card-${a.id}`}>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-50"><Edit3 className="w-4 h-4 text-purple-600" /></div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold text-gray-900">{a.oc_number}</h3>
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColors[a.status]}`}>{a.status}</span>
                          {a.applied_revision && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-purple-100 text-purple-700">Rev-{a.applied_revision}</span>}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-0.5">{a.customer_name} &middot; by <strong>{a.requested_by_name}</strong> &middot; {new Date(a.requested_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        <p className="text-[11px] text-gray-600 mt-0.5 font-medium">Reason: {a.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right mr-2">
                        <p className="text-[10px] text-gray-400">Value Change</p>
                        <p className={`text-xs font-bold ${a.proposed_total > a.original_total ? 'text-green-600' : a.proposed_total < a.original_total ? 'text-red-600' : 'text-gray-500'}`}>
                          {a.proposed_total > a.original_total ? '+' : ''}{((a.proposed_total || 0) - (a.original_total || 0)).toLocaleString()}
                        </p>
                      </div>
                      {a.status === 'Pending' && canApproveAmendment && (
                        <>
                          <button onClick={() => { setAmendApproveModal(a.id); setAmendApproveNotes(''); }} data-testid={`approve-amendment-${a.id}`}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                            {processing === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Approve
                          </button>
                          <button onClick={() => { setAmendRejectModal(a.id); setAmendRejectReason(''); }} data-testid={`reject-amendment-${a.id}`}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600">
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                      {a.status === 'Approved' && a.reviewed_by_name && <span className="text-[11px] text-emerald-600">by {a.reviewed_by_name}</span>}
                      {a.status === 'Rejected' && a.review_notes && (
                        <div className="flex items-center gap-1 text-[11px] text-red-500 max-w-[200px]" title={a.review_notes}><MessageSquare className="w-3 h-3 flex-shrink-0" /><span className="truncate">{a.review_notes}</span></div>
                      )}
                      <button onClick={() => setAmendExpanded(amendExpanded === a.id ? null : a.id)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        {amendExpanded === a.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                  {amendExpanded === a.id && (
                    <div className="px-4 pb-4 border-t border-gray-50 pt-3">
                      <p className="text-[11px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">Item Changes</p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 text-[9px] font-bold text-gray-500 uppercase">
                              <th className="text-left px-3 py-1.5">Product</th>
                              <th className="text-right px-3 py-1.5">Old Qty</th>
                              <th className="text-right px-3 py-1.5">New Qty</th>
                              <th className="text-right px-3 py-1.5">Old Rate</th>
                              <th className="text-right px-3 py-1.5">New Rate</th>
                              <th className="text-right px-3 py-1.5">Old Amt</th>
                              <th className="text-right px-3 py-1.5">New Amt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(a.proposed_items || []).map((pi, idx) => {
                              const oi = (a.original_items || [])[idx] || {};
                              const qtyChanged = oi.qty !== pi.qty;
                              const rateChanged = oi.rate !== pi.rate;
                              return (
                                <tr key={idx} className="border-t border-gray-100">
                                  <td className="px-3 py-1.5 font-medium">{pi.product_name || '-'}</td>
                                  <td className={`px-3 py-1.5 text-right ${qtyChanged ? 'text-red-400 line-through' : ''}`}>{oi.qty ?? '-'}</td>
                                  <td className={`px-3 py-1.5 text-right font-semibold ${qtyChanged ? 'text-green-600' : ''}`}>{pi.qty}</td>
                                  <td className={`px-3 py-1.5 text-right ${rateChanged ? 'text-red-400 line-through' : ''}`}>{oi.rate ?? '-'}</td>
                                  <td className={`px-3 py-1.5 text-right font-semibold ${rateChanged ? 'text-green-600' : ''}`}>{pi.rate}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-400">{((oi.qty || 0) * (oi.rate || 0)).toLocaleString()}</td>
                                  <td className="px-3 py-1.5 text-right font-semibold">{((pi.qty || 0) * (pi.rate || 0)).toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-gray-200 bg-gray-50">
                              <td colSpan={5} className="px-3 py-2 font-bold text-right">Total:</td>
                              <td className="px-3 py-2 text-right text-gray-500">{(a.original_total || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right font-bold" style={{ color: '#24549c' }}>{(a.proposed_total || 0).toLocaleString()}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {a.detail_changes?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">Other Changes</p>
                          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                            {a.detail_changes.map((d, di) => (
                              <div key={di} className="px-3 py-1.5 flex items-center justify-between text-[11px]" data-testid={`detail-change-${d.field}`}>
                                <span className="font-semibold capitalize text-gray-700">{d.field.replace(/_/g, ' ')}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-red-500 line-through max-w-[180px] truncate" title={d.old}>{d.old}</span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-green-700 font-semibold max-w-[180px] truncate" title={d.new}>{d.new}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          {a.detail_changes.some(d => d.field === 'customer') && (
                            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800">
                              <strong>Heads-up:</strong> approving this will recompute GST and <strong>clear any uploaded Tally invoice</strong> — sales must re-upload under the new customer.
                            </div>
                          )}
                        </div>
                      )}
                      {a.review_notes && (
                        <div className="mt-3 p-2 bg-gray-50 rounded-lg"><p className="text-[10px] font-semibold text-gray-500">Review Notes:</p><p className="text-xs text-gray-700">{a.review_notes}</p></div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Reject Modal (Customers) */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Reject Request</h2>
              <p className="text-xs text-gray-500 mt-0.5">Provide a reason for rejection</p>
            </div>
            <div className="p-5">
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason-input"
                placeholder="e.g., Incomplete KYC details, duplicate customer..." rows={3} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-200" />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleReject} disabled={processing} data-testid="confirm-reject-btn" className="px-4 py-2 text-xs font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2">
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Amendment Modal */}
      {amendApproveModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Approve Amendment</h2>
              <p className="text-xs text-gray-500 mt-0.5">This will apply the changes to the OC and increment the revision.</p>
            </div>
            <div className="p-5">
              <textarea value={amendApproveNotes} onChange={e => setAmendApproveNotes(e.target.value)} data-testid="approve-amendment-notes"
                placeholder="Optional approval notes..." rows={3} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-200" />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setAmendApproveModal(null)} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleApproveAmendment} disabled={!!processing} data-testid="confirm-approve-amendment-btn"
                className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Approve & Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Amendment Modal */}
      {amendRejectModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Reject Amendment</h2>
              <p className="text-xs text-gray-500 mt-0.5">Provide a reason for rejecting this amendment</p>
            </div>
            <div className="p-5">
              <textarea value={amendRejectReason} onChange={e => setAmendRejectReason(e.target.value)} data-testid="reject-amendment-notes"
                placeholder="e.g., Rate change not authorized by management..." rows={3} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-200" />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setAmendRejectModal(null)} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleRejectAmendment} disabled={!!processing} data-testid="confirm-reject-amendment-btn"
                className="px-4 py-2 text-xs font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2">
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalsModule;
